import React, {useMemo} from 'react';
import {Menu} from '@mantine/core';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  CourseClassroomSimpleResponse,
  CourseClassroomStateEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {CourseClassroomStatePill} from 'src/shared/constants/PillConstants';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetCourseClassroomListV2Query} from 'src/redux/queries/courseClassroom';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {ScheduleFilters} from './ScheduleFiltersBar';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ScheduleListViewProps {
  filters: ScheduleFilters;
}

const formatDateTime = (value: Date | string | undefined | null): string =>
  value ? parseUtcDate(value).toLocaleString() : '';

/**
 * All classrooms across every course — list view of the Schedule page.
 */
export const ScheduleListView: React.FC<ScheduleListViewProps> = ({filters}) => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<CourseClassroomSimpleResponse>[]>(
    () => [
      {accessorKey: 'courseDefinition.name', header: 'Course'},
      {
        id: 'startsAt',
        accessorFn: row => (row.startsAt ? parseUtcDate(row.startsAt) : null),
        header: 'Starts',
        Cell: ({cell}) => formatDateTime(cell.getValue<Date | null>()),
      },
      {accessorKey: 'location', header: 'Location'},
      {accessorKey: 'instructor.name', header: 'Instructor'},
      {
        id: 'enrolled',
        header: 'Capacity',
        accessorFn: row =>
          row.capacity !== undefined
            ? `${row.attendees?.length ?? 0} / ${row.capacity}`
            : `${row.attendees?.length ?? 0}`,
      },
      {
        accessorKey: 'state',
        header: 'State',
        Cell: ({cell}) => (
          <Pill {...CourseClassroomStatePill(cell.getValue<CourseClassroomStateEnum>())} />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-schedule-list',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'startsAt', desc: false}],
  });
  const {start, length, filters: mrtFilters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseClassroomListV2Query({
      start,
      length,
      filters: mrtFilters,
      filterFns,
      sorting,
      courseDefinitionId: filters.courseDefinitionId
        ? Number(filters.courseDefinitionId)
        : undefined,
      instructorId: filters.instructorId ? Number(filters.instructorId) : undefined,
      states: filters.states.length ? filters.states : undefined,
      startsAfter: filters.startsAfter?.toISOString(),
      startsBefore: filters.startsBefore?.toISOString(),
    });

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleOpen = (row: CourseClassroomSimpleResponse) =>
    navigate(
      PathUtility.TrainingCourseClassroomView(
          String(row.courseDefinitionId),
          String(row.id),
          'roster'
        )
    );

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowActions: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActionMenuItems: ({row}) => (
        <Menu.Item onClick={() => handleOpen(row.original)}>
          Open Class Workspace
        </Menu.Item>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
